import { Component, effect, inject, Injector, input, OnInit, TemplateRef } from '@angular/core';

import { AsyncPipe, KeyValuePipe } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';

import { BehaviorSubject, firstValueFrom } from 'rxjs';

import { GenericTableComponent, TableColumn } from '../../core/generic-table/generic-table.component';
import { SearchbarComponent } from '../../../common/components/searchbar/searchbar.component';
import { TonalButtonComponent } from '../../core/tonal-button/tonal-button.component';
import { TableSettingsComponent } from '../../core/table-settings/table-settings.component';
import { ButtonComponent } from '../../../common/components/button/button.component';

import { MatFormField, MatLabel, MatPrefix } from '@angular/material/form-field';
import { MatOption, MatSelect, MatSelectChange } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import { MatDialog } from '@angular/material/dialog';

import { FhirHealthcareService } from '../../../../shared-api/fhirHealthcareService';

import { MatIcon } from '@angular/material/icon';
import { VzdTokenService } from '../../../services/vzd-fhir/vzd-token.service';
import {
  SearchInFhirDirectoryService,
  VzdMappedResult
} from '../../../services/search-in-fhir-directory/search-in-fhir-directory.service';
import { SnackbarService } from '../../../services/snackbar/snackbar.service';
import { FhirEndpoint } from '../../../../shared-api/fhirEndpoint';
import { FhirOrganization } from '../../../../shared-api/fhirOrganization';
import { FhirSearchResult } from '../../../../shared-api/fhirSearchResult';
import { SettingsSliderComponent } from '../../../common/components/base-settings-dialog/settings-slider/settings-slider.component';
import { decodeJwt } from 'jose';
import { OrgAdminEndpointsService } from '../../../services/org-service/org-admin-endpoints.service';
import { UserResponse } from '../org-model/user-response.model';
import { OrgAdminFhirSharedService } from '../../../services/org-admin-fhir/org-admin-fhir-shared.service';
import { OrgHealthcareServiceTestIds } from './org-healthcare-service.test-ids';

@Component({
  selector: 'app-org-healthcare-service',
  templateUrl: './org-healthcare-service.component.html',
  styleUrls: ['./org-healthcare-service.component.scss'],
  imports: [
    AsyncPipe,
    GenericTableComponent,
    SearchbarComponent,
    TonalButtonComponent,
    TableSettingsComponent,
    MatFormField,
    MatSelect,
    MatOption,
    FormsModule,
    MatLabel,
    MatInput,
    ReactiveFormsModule,
    ButtonComponent,
    MatIcon,
    SettingsSliderComponent,
    KeyValuePipe,
    MatPrefix
  ]
})
/**
 * Komponente zur Anzeige der Healthcareservices und dem Bearbeiten Begründung:
 */
export class OrgHealthcareServiceComponent implements OnInit {
  displayedColumns: TableColumn<VzdMappedResult>[] = [];
  columsToDisplay: TableColumn<VzdMappedResult>[] = [];
  totalItems = 0;
  domain = input.required<string>();
  total = 0;
  pageSize = 10;
  pageIndex = 0;
  dir: 'asc' | 'desc' = 'asc';
  isLoading = false;
  filterKey = 'own';
  searchTerm = '';
  isCreate = false;
  selectedRessource:
    | {
        hcs: FhirHealthcareService | undefined;
        endpoints: FhirEndpoint[];
        organization: FhirOrganization | undefined;
      }
    | undefined;
  healthcareServiceForm: FormGroup;
  endpoints: FormGroup[] = [];
  protected readonly testIds = OrgHealthcareServiceTestIds;
  protected readonly matDialog = inject(MatDialog);
  private readonly dataSubject = new BehaviorSubject<VzdMappedResult[]>([]);
  dataSource$ = this.dataSubject.asObservable();
  private readonly injector = inject(Injector);
  private readonly fb = inject(FormBuilder);
  private readonly snackbarService = inject(SnackbarService);
  private readonly orgAdminFhirSharedService = inject(OrgAdminFhirSharedService);
  private readonly tokenService = inject(VzdTokenService);
  private readonly orgAdminEndpointService = inject(OrgAdminEndpointsService);
  private deleteEndpointList: string[] = [];
  private readonly searchInFhirDirectoryService = inject(SearchInFhirDirectoryService);
  private healthcareServiceSearchResult: FhirSearchResult = {
    resourceType: 'Bundle',
    total: 0,
    entry: []
  };

  constructor() {
    this.healthcareServiceForm = this.fb.group({
      items: this.fb.array([]),
      healthcareServiceName: ''
    });
  }

  ngOnInit(): void {
    this.displayedColumns = [
      { key: 'hsName', label: 'HCSname', sortable: true },
      { key: 'hsId', label: 'ID', sortable: true },
      { key: 'organizationName', label: 'Organisationsname', sortable: false },
      { key: 'organizationId', label: 'Organisations-ID' }
    ];

    this.columsToDisplay = this.displayedColumns.slice();

    effect(
      async () => {
        await this.loadHealthcareServices();
      },
      { injector: this.injector }
    );
  }

  async onSelect(event: { element: VzdMappedResult }): Promise<void> {
    if (!event.element.hsId) {
      this.snackbarService.error('Keine Id im ausgewählten Element');
      return;
    }
    if (event.element.hsId === this.selectedRessource?.hcs?.id) {
      this.selectedRessource = { hcs: undefined, organization: undefined, endpoints: [] };
      return;
    }
    const fhirHCSRessource = await this.orgAdminFhirSharedService.getHealthcareServiceWithResources(event.element.hsId);
    const endpoints: FhirEndpoint[] = [];
    let organization = undefined;
    let hcs = undefined;
    if (fhirHCSRessource.entry?.length === 0) {
      throw new Error(`Suche fehlgeschlagen mit Id ${event.element.hsId}`);
    }
    fhirHCSRessource.entry?.forEach(elem => {
      switch (elem.resource.resourceType) {
        case 'HealthcareService':
          hcs = elem.resource as FhirHealthcareService;
          break;
        case 'Organization':
          organization = elem.resource as FhirOrganization;
          break;
        case 'Endpoint':
          endpoints.push(elem.resource as FhirEndpoint);
          break;
      }
    });
    if (!hcs) {
      throw new Error('Es wurde kein Healthcareservice gefunden.');
    }
    this.selectedRessource = { hcs: hcs, endpoints: endpoints, organization: organization };
  }

  onPageChange(event: { pageIndex: number; pageSize: number }): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
  }

  onSortChange(event: { dir: string }): void {
    this.dir = event.dir === 'desc' ? 'desc' : 'asc';
    this.pageIndex = 0;
  }

  async onSearchTerms(term: string): Promise<void> {
    this.searchTerm = term ?? '';
    this.pageIndex = 0;
    await this.loadHealthcareServices();
  }

  async setFilter(event: MatSelectChange): Promise<void> {
    this.filterKey = event.value;
    await this.loadHealthcareServices();
  }

  async openDialog(template: TemplateRef<unknown>, create: boolean, $event?: Event): Promise<void> {
    this.isCreate = create;
    if (!create) {
      if (!this.selectedRessource?.hcs?.id) {
        this.snackbarService.error('Vor dem Anlegen eines Endpunktes muss ein Healthcareservice ausgewählt werden');
        return;
      }
      const token = await this.tokenService.getToken();
      const tid = decodeJwt(token)?.sub;

      const isOwnRessource = this.selectedRessource.organization?.identifier?.some(item => item.value === tid) ?? false;

      if (!isOwnRessource) {
        this.snackbarService.error('Bearbeiten von Fremden Ressourcen ist verboten');
        return;
      }

      this.resetEndpointForm(false);
      this.healthcareServiceForm = this.fb.group({ healthcareServiceName: this.selectedRessource.hcs?.name });
      this.initializeEndpointsForHealthcareService();
    } else {
      this.resetEndpointForm(false);
      this.selectedRessource = undefined;
    }
    this.matDialog.closeAll();
    this.matDialog.open(template, { width: '53rem' });
    if ($event) $event.stopPropagation();
  }

  async handleDialog(update: boolean): Promise<void> {
    try {
      if (update) {
        await this.updateHcs();
      } else {
        await this.createHCS();
      }
      this.matDialog.closeAll();
      await this.loadHealthcareServices();
      this.selectedRessource = { hcs: undefined, organization: undefined, endpoints: [] };
    } catch (error) {
      console.error('Fehler beim Speichern der HealthcareService:', error);
    }
  }

  async updateHcs(): Promise<void> {
    try {
      if (!this.selectedRessource?.hcs?.id) {
        throw new Error('Kein HealthcareService ausgewählt');
      }

      const originalHcs: FhirHealthcareService = await this.orgAdminFhirSharedService.getHealthcareService(
        this.selectedRessource?.hcs?.id
      );

      if (!originalHcs) {
        throw new Error('Healthcareservice konnte nicht abgerufen werden');
      }
      originalHcs.name = this.healthcareServiceForm.value['healthcareServiceName'];

      await this.orgAdminFhirSharedService.updateHealthcareService(originalHcs.id!, originalHcs);

      for (const endpoint of this.endpoints) {
        const address = endpoint.get('endpointAddress')?.value;
        const name = endpoint.get('endpointName')?.value;

        if (!address || !name) {
          continue;
        }

        const fhirEndpoint: FhirEndpoint = this.mapFormToFhirEndpoint(endpoint);
        if (!fhirEndpoint.id) {
          await this.orgAdminFhirSharedService.createEndpoint(originalHcs.id!, fhirEndpoint);
        } else {
          await this.orgAdminFhirSharedService.updateEndpoint(originalHcs.id!, fhirEndpoint.id, fhirEndpoint);
        }
      }

      if (this.deleteEndpointList.length > 0) {
        for (const ep of this.deleteEndpointList) {
          await this.orgAdminFhirSharedService.deleteEndpoint(originalHcs.id!, ep);
        }
      }

      this.snackbarService.success('Healthcareservice erfolgreich aktualisiert');
    } catch (error) {
      console.error('Error updating HCS:', error);
      this.snackbarService.error('Fehler beim Aktualisieren des Healthcareservices');
      throw error;
    }
  }

  async deleteHealthcareService(): Promise<void> {
    const token = await this.tokenService.getToken();
    const tid = decodeJwt(token)?.sub;

    if (!this.selectedRessource) {
      this.snackbarService.error('Keine Healthcareservice ausgewählt');
      return;
    }

    if (this.selectedRessource?.organization?.identifier?.[0].value !== tid) {
      this.snackbarService.error('Löschen von Fremden Ressourcen ist verboten');
      return;
    }

    if (!this.selectedRessource.hcs?.id) {
      this.snackbarService.error('Die ausgewählte Resource hat keine ID');
      return;
    }
    try {
      await this.orgAdminFhirSharedService.deleteHealthcareService(this.selectedRessource.hcs?.id);
      this.snackbarService.success('Healthcareservice erfolgreich entfernt');
    } catch (e) {
      this.snackbarService.error('Healthcareservice entfernen fehlgeschlagen');
      throw e;
    }
    this.selectedRessource = undefined;
    await this.loadHealthcareServices();
  }

  async createHCS(): Promise<void> {
    try {
      const newHcs: FhirHealthcareService = {
        resourceType: 'HealthcareService',
        name: this.healthcareServiceForm.value['healthcareServiceName']
      };
      const response = await this.orgAdminFhirSharedService.createHealthcareService(newHcs);

      for (const endpoint of this.endpoints) {
        const address = endpoint.get('endpointAddress')?.value;
        const name = endpoint.get('endpointName')?.value;

        if (address && name && response.id) {
          const fhirEndpoint: FhirEndpoint = this.mapFormToFhirEndpoint(endpoint);
          await this.orgAdminFhirSharedService.createEndpoint(response.id, fhirEndpoint);
        }
      }

      this.snackbarService.success('Healthcareservice erfolgreich erstellt');
    } catch (error) {
      this.snackbarService.error('Fehler beim Erstellen des Healthcareservices');
      throw error;
    }
  }

  resetEndpointForm(close = true): void {
    this.healthcareServiceForm.reset({
      // name: '',
      // connectionType: 'tim-fa',
      // mxid: '',
      // description: '',
      healthcareServiceName: ''
    });
    this.endpoints = [];
    this.deleteEndpointList = [];

    if (close) {
      this.matDialog.closeAll();
    }
  }

  removeEndpoint(index: number): void {
    this.deleteEndpointList.push(this.endpoints[index].value['id']);
    this.endpoints.splice(index, 1);
  }

  addEndpoint(): void {
    const endpointForm = this.fb.group({
      endpointName: ['', Validators.required],
      endpointAddress: ['', Validators.required],
      connectionType: ['tim-fa', Validators.required],
      hideFromInsurees: [false]
    });
    endpointForm.controls.endpointName.disable();
    this.endpoints.push(endpointForm);
  }

  updateHideFromInsurees(endpoint: FormGroup, value: boolean): void {
    endpoint.get('hideFromInsurees')?.setValue(value);
  }

  protected async onBlur(endpoint: FormGroup): Promise<void> {
    if (!endpoint.get('endpointAddress')?.value) {
      return;
    }
    const endpointAdressControl = endpoint.get('endpointAddress');
    let endpointAddress = endpointAdressControl?.value;
    let userResponse: UserResponse | undefined;
    if (endpointAddress[0] !== '@') {
      endpointAddress = '@' + endpointAddress;
    }
    try {
      userResponse = await firstValueFrom(this.orgAdminEndpointService.getUser(this.domain(), endpointAddress));
    } catch {
      endpointAdressControl?.setErrors({ invalidMatrixId: `Ungültige MatrixId: ${endpointAddress}` });
    }
    const displayname = userResponse?.result.displayname;
    endpoint.get('endpointName')?.patchValue(displayname);
  }

  private mapFormToFhirEndpoint(endpoint: FormGroup): FhirEndpoint {
    let value = endpoint.get('endpointAddress')?.value;
    if (value[0] === '@') {
      value = value.replace('@', 'matrix:u/');
    } else {
      value = 'matrix:u/' + value;
    }
    const fhirEndpoint = {
      resourceType: 'Endpoint',
      name: endpoint.get('endpointName')?.value,
      address: value,
      id: endpoint.value['id'] ?? undefined,
      meta: {
        profile: ['https://gematik.de/fhir/directory/StructureDefinition/EndpointDirectory'],
        tag: [{ code: 'owner', system: 'https://gematik.de/fhir/directory/CodeSystem/Origin' }]
      },
      status: 'active',
      connectionType: {
        code: endpoint.value['connectionType'],
        system: 'https://gematik.de/fhir/directory/CodeSystem/EndpointDirectoryConnectionType'
      },
      payloadType: [
        {
          coding: [
            {
              code: 'tim-chat',
              system: 'https://gematik.de/fhir/directory/CodeSystem/EndpointDirectoryPayloadType'
            }
          ]
        }
      ]
    } as FhirEndpoint;

    if (endpoint.value['hideFromInsurees'] === true) {
      fhirEndpoint.extension = [
        {
          url: 'https://gematik.de/fhir/directory/StructureDefinition/EndpointVisibility',
          valueCoding: {
            code: 'hide-versicherte',
            system: 'https://gematik.de/fhir/directory/CodeSystem/EndpointVisibilityCS'
          }
        }
      ];
    }
    return fhirEndpoint;
  }

  private initializeEndpointsForHealthcareService(): void {
    if (!this.healthcareServiceSearchResult?.entry) {
      throw new Error('No healthcareServiceResult or entry found');
    }

    const healthcareService = this.healthcareServiceSearchResult.entry.findLast(
      entry =>
        entry.resource.resourceType === 'HealthcareService' && entry.resource.id === this.selectedRessource?.hcs?.id
    );

    let endpoints: FhirEndpoint[] = [];

    if (healthcareService) {
      const endpointRefs = healthcareService.resource.endpoint ?? [];
      const endpointIds = new Set(endpointRefs.map(ref => ref.reference.split('/').pop()!));
      endpoints = this.healthcareServiceSearchResult.entry
        .filter(
          entry =>
            entry.resource?.resourceType === 'Endpoint' && entry.resource?.id && endpointIds.has(entry.resource.id)
        )
        .map(entry => entry.resource as FhirEndpoint);
    }

    endpoints.forEach(ep => {
      let hideFromInsurees = false;
      if (
        ep.extension?.filter(
          ext =>
            ext.url === 'https://gematik.de/fhir/directory/StructureDefinition/EndpointVisibility' &&
            ext.valueCoding.code === 'hide-versicherte'
        )
      ) {
        hideFromInsurees = true;
      }
      const endpointForm = this.fb.group({
        id: ep.id ?? '',
        endpointName: ep.name || '',
        endpointAddress: this.truncatePrefix(ep.address) || '',
        connectionType: ep.connectionType?.code,
        hideFromInsurees: hideFromInsurees
      });
      endpointForm.get('endpointName')?.disable();
      this.endpoints.push(endpointForm);
    });
  }

  private truncatePrefix(adress: string): string {
    if (adress.startsWith('matrix:u/')) {
      return adress.slice(9);
    }
    return adress;
  }

  private async loadHealthcareServices(): Promise<void> {
    this.isLoading = true;

    if (this.filterKey === 'own') {
      this.healthcareServiceSearchResult = await this.orgAdminFhirSharedService.getHealthcareServices(this.searchTerm);
    } else {
      this.healthcareServiceSearchResult = await this.searchInFhirDirectoryService.searchOrganizationInFhirDirectory(
        this.searchTerm
      );
    }
    const mappedResult = this.searchInFhirDirectoryService.mapOrganizationResult(this.healthcareServiceSearchResult);
    this.isLoading = false;
    this.dataSubject.next(mappedResult);
  }
}
